import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { Coins, TrendingUp, ArrowLeft, Leaf, Wallet, Target, Hash } from 'lucide-react';
import MoringaCityMap from '../components/MoringaCityMap';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/moringa-logo.png';

const fmtUSD = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MoringaDashboardPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const pt = language === 'pt';
  const [me, setMe] = useState(null);
  const [cfg, setCfg] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/moringa/me`).then((r) => setMe(r.data)).catch(() => {}),
      axios.get(`${API_URL}/moringa/public`).then((r) => setCfg(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const T = pt
    ? { back: 'Voltar ao painel', title: 'Meu Investimento Moringa', pos: 'Posição Atual', mbt: 'Tokens MBT', ret: 'Retorno Mensal Alvo', status: 'Status', invest_more: 'Investir Mais', none: 'Você ainda não possui investimentos no Moringa.', start: 'Começar a Investir', myrefs: 'Meus Aportes', map_title: 'Rede de Produção — Nordeste', map_sub: 'Distribuição dos seus hectares por cidade e projeto.', growth: 'Crescimento da Produção', active: 'ATIVO', net: 'Rede Agrícola (ha)', rev: 'Receita (US$)' }
    : { back: 'Back to dashboard', title: 'My Moringa Investment', pos: 'Current Position', mbt: 'MBT Tokens', ret: 'Target Monthly Return', status: 'Status', invest_more: 'Invest More', none: 'You have no Moringa investments yet.', start: 'Start Investing', myrefs: 'My Allocations', map_title: 'Production Network — Northeast', map_sub: 'Distribution of your hectares by city and project.', growth: 'Production Growth', active: 'ACTIVE', net: 'Agricultural Network (ha)', rev: 'Revenue (US$)' };

  const invested = me?.total_invested || 0;
  const hasPosition = invested > 0;

  return (
    <div className="min-h-screen bg-[#07160f] text-white" data-testid="moringa-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#07160f]/80 border-b border-[#e9c767]/15">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Moringa" className="h-9 w-auto" />
            <span className="text-sm font-semibold text-[#e9c767] hidden sm:block">MORINGA AGRO INVEST</span>
          </div>
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-emerald-100/70 hover:text-[#e9c767] transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> {T.back}
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10 space-y-10">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#e9c767]">{user?.first_name ? `${user.first_name},` : ''}</p>
          <h1 className="text-3xl md:text-4xl font-extrabold">{T.title}</h1>
        </div>

        {/* Position cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Wallet, label: T.pos, value: fmtUSD(invested), gold: false },
            { icon: Coins, label: T.mbt, value: `${(me?.mb_tokens || 0).toLocaleString('en-US')} ${me?.mb_symbol || 'MBT'}`, gold: true },
            { icon: Target, label: T.ret, value: `${me?.target_return_min ?? 2}% – ${me?.target_return_max ?? 3}%`, gold: false },
            { icon: Hash, label: T.status, value: hasPosition ? T.active : '—', gold: false },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-[#e9c767]/15 bg-gradient-to-br from-[#0a1f15] to-[#07160f] p-5" data-testid={`position-card-${i}`}>
              <s.icon className="w-5 h-5 text-[#e9c767] mb-3" />
              <p className="text-[11px] uppercase tracking-[0.15em] text-emerald-200/50">{s.label}</p>
              <p className={`mt-1 text-xl md:text-2xl font-extrabold ${s.gold ? 'text-[#e9c767]' : 'text-white'}`}>{s.value}</p>
            </motion.div>
          ))}
        </div>

        {!loading && !hasPosition && (
          <div className="rounded-2xl border border-[#e9c767]/20 bg-[#0a1f15] p-8 text-center">
            <p className="text-emerald-100/70">{T.none}</p>
            <Link to="/dashboard" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#e9c767] px-6 py-3 text-sm font-bold text-[#07160f] hover:brightness-110">{T.start}</Link>
          </div>
        )}

        {/* Allocations */}
        {hasPosition && (me?.investments || []).length > 0 && (
          <div className="rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-6">
            <h3 className="text-lg font-bold mb-4">{T.myrefs}</h3>
            <div className="space-y-2">
              {(me.investments || []).map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#e9c767]/10 bg-[#07160f]/60 px-4 py-3" data-testid={`alloc-${inv.investment_ref}`}>
                  <span className="font-mono text-xs text-[#e9c767]">{inv.investment_ref}</span>
                  <span className="text-sm font-semibold">{fmtUSD(inv.amount_usd)}</span>
                  <span className="text-sm text-[#e9c767]">{(inv.mb_tokens || 0).toLocaleString('en-US')} MBT</span>
                  <span className="text-xs text-emerald-200/50">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3D City Map */}
        <div>
          <div className="flex items-center gap-3 mb-2"><Leaf className="w-5 h-5 text-[#e9c767]" /><h2 className="text-2xl md:text-3xl font-bold">{T.map_title}</h2></div>
          <p className="text-emerald-100/60 mb-8">{T.map_sub}</p>
          <MoringaCityMap
            cities={cfg.cities || []}
            labels={{ hint: pt ? 'Passe o mouse sobre uma torre para ver os dados do projeto' : 'Hover a city tower to see project details', share: pt ? 'Alocação' : 'Allocation', hectares: 'Hectares', planting: pt ? 'Plantio' : 'Planting', planted: pt ? 'Plantado' : 'Planted', harvest: pt ? 'Próxima colheita' : 'Next harvest', yield: pt ? 'Produção estimada' : 'Expected yield' }}
          />
        </div>

        {/* Growth charts */}
        {(cfg.growth || []).length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-6"><TrendingUp className="w-5 h-5 text-[#e9c767]" /><h2 className="text-2xl md:text-3xl font-bold">{T.growth}</h2></div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-6">
                <p className="text-xs uppercase tracking-[0.15em] text-emerald-200/60 mb-4">{T.net}</p>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={cfg.growth}>
                    <defs><linearGradient id="dNet" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3ba776" stopOpacity={0.6} /><stop offset="100%" stopColor="#3ba776" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                    <XAxis dataKey="period" stroke="rgba(255,255,255,0.4)" fontSize={10} interval={3} />
                    <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ background: '#07160f', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12 }} />
                    <Area type="monotone" dataKey="hectares" stroke="#3ba776" strokeWidth={2.5} fill="url(#dNet)" animationDuration={1400} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-[#e9c767]/15 bg-[#0a1f15] p-6">
                <p className="text-xs uppercase tracking-[0.15em] text-emerald-200/60 mb-4">{T.rev}</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={cfg.growth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(233,199,103,0.08)" />
                    <XAxis dataKey="period" stroke="rgba(255,255,255,0.4)" fontSize={10} interval={3} />
                    <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickFormatter={(v) => `$${(v / 1000000).toFixed(0)}M`} />
                    <Tooltip contentStyle={{ background: '#07160f', border: '1px solid rgba(233,199,103,0.3)', borderRadius: 12 }} formatter={(v) => `$${Number(v).toLocaleString('en-US')}`} />
                    <Bar dataKey="revenue" radius={[6, 6, 0, 0]} animationDuration={1400}>
                      {(cfg.growth || []).map((_, i) => (<Cell key={i} fill="#e9c767" fillOpacity={0.55 + i * 0.09} />))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
